
import React, { useState, useEffect } from 'react';
import { MemberModel, SiteSettingModel, BranchModel } from '../../../../models';
import { db, auth } from '../../../../firebase';
import { doc, onSnapshot, collection, query, getDocs, getDoc } from 'firebase/firestore';
import StudentRoot from '../../student/StudentRoot';

interface StudentViewPageProps {
    memberId: string;
}

const StudentViewPage: React.FC<StudentViewPageProps> = ({ memberId }) => {
    const [member, setMember] = useState<MemberModel | null>(null);
    const [siteSettings, setSiteSettings] = useState<SiteSettingModel>({
        subjects: [],
        grades: [],
        bookTypes: [],
        publishers: [],
        examTypes: []
    });
    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>("");

    useEffect(() => {
        let unsubscribeMember: (() => void) | undefined;

        const init = async () => {
            // Wait for Auth to initialize
            const user = await new Promise<any>((resolve) => {
                const unsub = auth.onAuthStateChanged((u) => {
                    unsub();
                    resolve(u);
                });
            });

            // Parse key from URL
            const params = new URLSearchParams(window.location.search);
            const accessKey = params.get('key');

            // Fetch member
            unsubscribeMember = onSnapshot(doc(db, 'members', memberId), async (snap) => {
                if (snap.exists()) {
                    const memberData = { ...snap.data(), id: snap.id } as MemberModel;
                    setMember(memberData);

                    // Check Access
                    // 1. If key is present, validate it
                    if (accessKey) {
                        if (memberData.parentAccessKey === accessKey) {
                            setIsReadOnly(true);
                            setIsLoading(false);
                            return;
                        } else {
                            setDebugInfo(`Invalid Access Key: ${accessKey}`);
                            setAccessDenied(true);
                            setIsLoading(false);
                            return;
                        }
                    }

                    // 2. If no key, requires authentication
                    if (user) {
                        // Check if user is the student themselves
                        if (user.uid === memberData.id) {
                            setIsLoading(false);
                            return;
                        }

                        // Check Admin/Manager/Staff permissions
                        try {
                            const currentUserRef = doc(db, 'members', user.uid);
                            const currentUserSnap = await getDoc(currentUserRef);

                            if (currentUserSnap.exists()) {
                                const currentUserData = currentUserSnap.data() as MemberModel;
                                const allowedRoles = ['super_admin', 'teacher'];
                                const isAllowedRole = allowedRoles.includes(currentUserData.role);
                                const isSameBranch = true;

                                if (isAllowedRole) {
                                    // Access Granted
                                    setIsLoading(false);
                                } else {
                                    const debugMsg = `Role: ${currentUserData.role}, Allowed: ${isAllowedRole}`;
                                    console.log("Access Denied:", debugMsg);
                                    setDebugInfo(debugMsg);
                                    setAccessDenied(true);
                                    setIsLoading(false);
                                }
                            } else {
                                const debugMsg = `Admin profile not found for uid: ${user.uid}`;
                                console.log("Access Denied:", debugMsg);
                                setDebugInfo(debugMsg);
                                setAccessDenied(true);
                                setIsLoading(false);
                            }
                        } catch (e: any) {
                            const debugMsg = `Error checking permissions: ${e.message}`;
                            console.error("Error checking permissions", e);
                            setDebugInfo(debugMsg);
                            setAccessDenied(true);
                            setIsLoading(false);
                        }
                    } else {
                        // Not logged in and no key
                        const debugMsg = "Not logged in (user is null)";
                        console.log("Access Denied:", debugMsg);
                        setDebugInfo(debugMsg);
                        setAccessDenied(true);
                        setIsLoading(false);
                    }
                } else {
                    // Member not found
                    const debugMsg = `Member document not found: ${memberId}`;
                    console.log("Access Denied:", debugMsg);
                    setDebugInfo(debugMsg);
                    setAccessDenied(true);
                    setIsLoading(false);
                }
            }, (error) => {
                const debugMsg = `Firestore Error: ${error.message}`;
                console.error("Error fetching member:", error);
                setDebugInfo(debugMsg);
                setAccessDenied(true);
                setIsLoading(false);
            });
        };

        const loadGlobals = async () => {
            const settingsSnap = await getDocs(collection(db, 'siteSettings'));
            if (!settingsSnap.empty) {
                setSiteSettings(settingsSnap.docs[0].data() as SiteSettingModel);
            }
            const branchesSnap = await getDocs(collection(db, 'branches'));
            const list: BranchModel[] = [];
            branchesSnap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as BranchModel));
            setBranches(list);
        };

        init();
        loadGlobals();

        return () => {
            if (unsubscribeMember) unsubscribeMember();
        };
    }, [memberId]);

    if (accessDenied) {
        return (
            <div className="w-screen h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
                <div className="text-center space-y-4">
                    <p className="text-rose-600 font-bold text-lg">접근 권한이 없습니다.</p>
                    <p className="text-slate-500">올바른 링크인지 확인해주세요.</p>
                </div>
                {debugInfo && (
                    <div className="p-4 bg-gray-100 rounded text-xs text-slate-500 font-mono max-w-lg break-all">
                        DEBUG: {debugInfo}
                    </div>
                )}
            </div>
        );
    }

    if (isLoading || !member) {
        return (
            <div className="w-screen h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
                    <p className="text-slate-600 font-bold">학생 포털 로딩 중...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-screen h-screen overflow-hidden">
            <StudentRoot
                member={member}
                siteSettings={siteSettings}
                branches={branches}
                isReadOnly={isReadOnly}
            />
        </div>
    );
};

export default StudentViewPage;
